import { create } from "zustand";

/** 설정이 저장되는 대상. 한 창의 목록 안에서 앱·워크스페이스·플랜 항목을 섞지 않는다. */
export type SettingsScope = "app" | "workspace" | "plan";

/** 앱과 워크스페이스는 설정 창. 플랜은 플랜이 열려 있을 때만 여는 별도 창. */
export type SettingsSurface = "settings" | "plan";

export type SettingsSection = {
  id: string;
  label: string;
};

export const APP_SETTINGS_SECTIONS: SettingsSection[] = [
  { id: "appearance", label: "모양" },
  { id: "account", label: "프로필" },
  { id: "security", label: "보안" },
  { id: "view", label: "보기" },
  { id: "shortcuts", label: "단축키" },
  { id: "integration", label: "MCP 연동" },
  { id: "logs", label: "로그" },
];

export const WORKSPACE_SETTINGS_SECTIONS: SettingsSection[] = [
  { id: "general", label: "일반" },
  { id: "invites", label: "초대" },
  { id: "access", label: "접근" },
  { id: "audit", label: "감사 로그" },
  { id: "danger", label: "위험 구역" },
];

export const PLAN_SETTINGS_SECTIONS: SettingsSection[] = [
  { id: "general", label: "일반" },
  { id: "schedule", label: "일정" },
  { id: "calendar", label: "캘린더" },
  { id: "buckets", label: "버킷·라벨" },
  { id: "members", label: "멤버" },
  { id: "security", label: "보안" },
  { id: "share", label: "공유" },
  { id: "audit", label: "감사 로그" },
];

const SCOPE_TITLE: Record<SettingsScope, string> = {
  app: "앱 설정",
  workspace: "워크스페이스 설정",
  plan: "플랜 설정",
};

const SCOPE_SHORT: Record<SettingsScope, string> = {
  app: "앱",
  workspace: "워크스페이스",
  plan: "플랜",
};

const DEFAULT_SECTION: Record<SettingsScope, string> = {
  app: "appearance",
  workspace: "general",
  plan: "general",
};

export function settingsScopeTitle(scope: SettingsScope): string {
  return SCOPE_TITLE[scope];
}

export function settingsSurface(scope: SettingsScope): SettingsSurface {
  return scope === "plan" ? "plan" : "settings";
}

export function settingsSurfaceScopes(surface: SettingsSurface): SettingsScope[] {
  return surface === "plan" ? ["plan"] : ["app", "workspace"];
}

/** 목록 묶음 이름. 앱은 「앱」, 워크스페이스와 플랜은 그 대상의 이름. */
export function settingsScopeLabel(scope: SettingsScope, names: { workspace?: string; plan?: string } = {}): string {
  if (scope === "app") return "앱";
  if (scope === "workspace") return names.workspace?.trim() || "워크스페이스";
  return names.plan?.trim() || "플랜";
}

export function settingsSections(scope: SettingsScope): SettingsSection[] {
  if (scope === "app") return APP_SETTINGS_SECTIONS;
  if (scope === "workspace") return WORKSPACE_SETTINGS_SECTIONS;
  return PLAN_SETTINGS_SECTIONS;
}

export function settingsSectionLabel(scope: SettingsScope, section: string): string {
  return settingsSections(scope).find((item) => item.id === section)?.label ?? settingsScopeTitle(scope);
}

/** 목록에 없는 항목은 그 대상의 기본 항목으로 돌린다. */
export function resolveSettingsSection(scope: SettingsScope, section?: string): string {
  const allowed = settingsSections(scope);
  if (section && allowed.some((item) => item.id === section)) return section;
  return DEFAULT_SECTION[scope];
}

/** 설정 모달의 주소. `/settings/workspace/audit` */
export function settingsPath(scope: SettingsScope, section: string): string {
  return `/settings/${scope}/${resolveSettingsSection(scope, section)}`;
}

/** 모달에 보이는 경로. 「워크스페이스 · 감사 로그」 */
export function settingsTrail(scope: SettingsScope, section: string): string {
  return `${SCOPE_SHORT[scope]} · ${settingsSectionLabel(scope, resolveSettingsSection(scope, section))}`;
}

const SETTINGS_SCOPES: readonly SettingsScope[] = ["app", "workspace", "plan"];

/** `/settings/:scope/:section`. 없는 항목은 그 대상의 기본 항목이다. */
export function parseSettingsPath(pathname: string): { scope: SettingsScope; section: string } | null {
  const [root, rawScope, rawSection, extra] = pathname.split("/").filter(Boolean);
  if (root !== "settings" || extra) return null;
  if (!SETTINGS_SCOPES.includes(rawScope as SettingsScope)) return null;
  const scope = rawScope as SettingsScope;
  const allowed = settingsSections(scope);
  const section = rawSection && allowed.some((item) => item.id === rawSection) ? rawSection : DEFAULT_SECTION[scope];
  return { scope, section };
}

type HubState = {
  scope: SettingsScope | null;
  section: string;
  /** false를 반환하면 나가지 않는다. 저장하지 않은 플랜 일반 설정이 이 자리를 쓴다. */
  closeGuard: (() => boolean) | null;
  /** false를 반환하면 항목을 바꾸지 않는다. 두 번째 인자는 이동할 대상이다. */
  sectionGuard: ((section: string, scope: SettingsScope) => boolean) | null;
  open: (scope: SettingsScope, section?: string) => void;
  close: () => void;
  forceClose: () => void;
  setSection: (section: string, scope?: SettingsScope) => void;
  commitSection: (section: string, scope?: SettingsScope) => void;
  setCloseGuard: (guard: (() => boolean) | null) => void;
  setSectionGuard: (guard: ((section: string, scope: SettingsScope) => boolean) | null) => void;
};

export const useSettingsHub = create<HubState>((set, get) => ({
  scope: null,
  section: "appearance",
  closeGuard: null,
  sectionGuard: null,
  open: (scope, section) => {
    const current = get();
    if (current.scope && current.scope !== scope) {
      const guard = current.closeGuard;
      if (guard && !guard()) return;
    }
    set({ scope, section: resolveSettingsSection(scope, section), closeGuard: null, sectionGuard: null });
  },
  close: () => {
    const guard = get().closeGuard;
    if (guard && !guard()) return;
    set({ scope: null, closeGuard: null, sectionGuard: null });
  },
  forceClose: () => set({ scope: null, closeGuard: null, sectionGuard: null }),
  setSection: (section, scope) => {
    const current = get();
    const nextScope = scope ?? current.scope;
    if (!nextScope) return;
    if (current.scope === nextScope && current.section === section) return;
    const guard = current.sectionGuard;
    if (guard && !guard(section, nextScope)) return;
    set({ section, scope: nextScope });
  },
  commitSection: (section, scope) => set(scope ? { section, scope } : { section }),
  setCloseGuard: (closeGuard) => set({ closeGuard }),
  setSectionGuard: (sectionGuard) => set({ sectionGuard }),
}));
